import { SwitchProductModule } from '@wix/package-picker-sdk';
import { Step } from '@wix/package-picker-shared';
import {
  useAnonymous,
  useIsStudioNewUi,
} from '@wix/package-picker-shared-studio';
import { StudioPackagePickerModule } from '../../modules/studio-package-picker.module';

export const TITLE_KEYS = {
  PLANS_PAGE: 'studio_package_picker.header.default.title',
  ANONYMOUS_PLANS_PAGE: 'studio_package_picker.anon.main_header',
  UPGRADE: 'premium-purchase-plan.studio.second_upgrade.title',
  DOWNGRADE: 'change_plan.app_title',
  CYCLES_PAGE: 'subscription_page.advanced_plans_title',
  CHECKOUT_PAGE: 'checkout.title',
};

export const NEW_UI_CYCLES_PAGE_TITLE_KEY =
  'premium-purchase-plan.select_cycle.title';

/**
 * Studio Two copy for the plans-page titles — same sentences with "Wix Studio"
 * replaced by "Studio Two". Used in place of TITLE_KEYS while the
 * `titleStudioTwo` spec is open. Titles shared with non-Studio flows (cycles,
 * checkout, downgrade) are inherited unchanged.
 */
const STUDIO_TWO_TITLE_KEYS: typeof TITLE_KEYS = {
  ...TITLE_KEYS,
  PLANS_PAGE: 'studio_package_picker.header.default.title_studio_2',
  ANONYMOUS_PLANS_PAGE: 'studio_package_picker.anon.main_header_studio_2',
  UPGRADE: 'premium-purchase-plan.studio.second_upgrade.title_studio_2',
};

const DEFAULT_STEPS_KEYS: Record<Step, string> = {
  'select-plan': TITLE_KEYS.PLANS_PAGE,
  'select-cycle': TITLE_KEYS.CYCLES_PAGE,
  checkout: TITLE_KEYS.CHECKOUT_PAGE,
  '*': TITLE_KEYS.PLANS_PAGE,
};

const STUDIO_TWO_STEPS_KEYS: Record<Step, string> = {
  'select-plan': STUDIO_TWO_TITLE_KEYS.PLANS_PAGE,
  'select-cycle': STUDIO_TWO_TITLE_KEYS.CYCLES_PAGE,
  checkout: STUDIO_TWO_TITLE_KEYS.CHECKOUT_PAGE,
  '*': STUDIO_TWO_TITLE_KEYS.PLANS_PAGE,
};

export const useTitleKey = () => {
  const step = StudioPackagePickerModule.internals.hooks.useCurrentStep();
  const { isAnonymous } = useAnonymous();
  const { experiments } =
    StudioPackagePickerModule.internals.experiments.hooks.useExperiments();
  const { isUpgradeFlow, isDowngradeFlow, isLoading } =
    SwitchProductModule.SDK.hooks.useSwitchFlows();

  const isTitleStudioTwo = experiments.isEnabled('titleStudioTwo');
  const titleKeys = isTitleStudioTwo ? STUDIO_TWO_TITLE_KEYS : TITLE_KEYS;
  const stepsKeys = isTitleStudioTwo
    ? STUDIO_TWO_STEPS_KEYS
    : DEFAULT_STEPS_KEYS;

  const isStudioNewUi = useIsStudioNewUi();
  const isCycleStep = step === 'select-cycle';
  const selectedProductId =
    StudioPackagePickerModule.SDK.PackagePickerStore.useSelectedProductId();
  const shouldFetchProduct = Boolean(
    isStudioNewUi && isCycleStep && selectedProductId,
  );
  const { data: selectedProduct, isLoading: isProductLoading } =
    StudioPackagePickerModule.SDK.studioOfferingApiClient.hooks.useFetchProduct(
      { productId: selectedProductId ?? '' },
      shouldFetchProduct,
    );

  let key = stepsKeys[step];

  if (isStudioNewUi && isCycleStep) {
    return {
      key: NEW_UI_CYCLES_PAGE_TITLE_KEY,
      params: { planName: selectedProduct?.name },
      isLoading: isLoading || (shouldFetchProduct && isProductLoading),
    };
  }

  if (step !== 'select-plan') {
    return { key, isLoading };
  }

  if (isAnonymous) {
    key = titleKeys.ANONYMOUS_PLANS_PAGE;
  }

  if (isUpgradeFlow) {
    key = titleKeys.UPGRADE;
  }

  if (isDowngradeFlow) {
    key = titleKeys.DOWNGRADE;
  }

  return { key, isLoading };
};
